import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Calendar,
  Users,
  DollarSign,
  Search,
  Filter,
  Plus,
  Edit2,
  Save,
  X,
  Clock,
  UserPlus,
  MessageSquare,
  CreditCard,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import ExtrasModal from "../components/ExtrasModal";
import EmployeeModal from "../components/EmployeeModal";
import "../styles/CompanySalaries.css";

const CompanySalaries = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const urlMonth = searchParams.get("month");
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedPaymentType, setSelectedPaymentType] = useState("");
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editingToPay, setEditingToPay] = useState({}); // Track to_pay editing
  const [editingReceipt, setEditingReceipt] = useState({}); // Track receipt_number editing
  const [extrasModalOpen, setExtrasModalOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [notesPopupOpen, setNotesPopupOpen] = useState(false);
  const [notesPopupData, setNotesPopupData] = useState(null);
  const [paymentPopup, setPaymentPopup] = useState({
    visible: false,
    salary: null,
    position: { x: 0, y: 0 },
  });
  const [newSalaryForm, setNewSalaryForm] = useState({
    employee_id: "",
    regular_hours: "",
    weekend_hours: "",
    salary_amount: "",
  });
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    // If URL month is provided, use it directly without fetching available months
    if (urlMonth) {
      setSelectedMonth(urlMonth);
    } else {
      loadAvailableMonths();
    }
    loadCompanyEmployees();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadSalaries();
      loadSummary();
      // Load available months in background for the dropdown (only if not already loaded)
      if (availableMonths.length === 0) {
        loadAvailableMonthsForDropdown();
      }
    }
  }, [selectedMonth]);

  const loadAvailableMonthsForDropdown = async () => {
    try {
      const [dashboardResponse, presenceResponse] = await Promise.all([
        apiService
          .getAvailableMonths()
          .catch(() => ({ success: false, data: [] })),
        apiService
          .getPresenceAvailableMonths()
          .catch(() => ({ success: false, months: [] })),
      ]);
      const dashboardMonths = dashboardResponse.data || [];
      const presenceMonths = presenceResponse.months || [];
      const allMonths = [
        ...new Set([...dashboardMonths, ...presenceMonths]),
      ].sort((a, b) => b.localeCompare(a));
      if (allMonths.length > 0) {
        setAvailableMonths(allMonths);
      }
    } catch (error) {
      console.error("Failed to load available months:", error);
    }
  };

  const loadAvailableMonths = async () => {
    try {
      const [dashboardResponse, presenceResponse] = await Promise.all([
        apiService
          .getAvailableMonths()
          .catch(() => ({ success: false, data: [] })),
        apiService
          .getPresenceAvailableMonths()
          .catch(() => ({ success: false, months: [] })),
      ]);
      const dashboardMonths = dashboardResponse.data || [];
      const presenceMonths = presenceResponse.months || [];
      const allMonths = [
        ...new Set([...dashboardMonths, ...presenceMonths]),
      ].sort((a, b) => b.localeCompare(a));
      if (allMonths.length > 0) {
        setAvailableMonths(allMonths);
        setSelectedMonth(allMonths[0]);
      }
    } catch (error) {
      console.error("Failed to load available months:", error);
    }
  };

  const loadCompanyEmployees = async () => {
    try {
      const response = await apiService.getEmployees({ limit: 10000 });
      console.log("Employee API response:", response);

      // Handle different response structures
      const employeesList = response.employees || response.data || [];

      // Get all employees and log types for debugging
      console.log(
        "All employees:",
        employeesList.map((e) => ({ name: e.employee_name, type: e.type })),
      );
      setEmployees(employeesList);
    } catch (error) {
      console.error("Failed to load employees:", error);
    }
  };

  const loadSalaries = async () => {
    try {
      setLoading(true);
      console.log(
        "Loading company salaries for month:",
        selectedMonth,
        "partnership:",
        user?.partnership_id || 1,
      );
      const response = await apiService.getCompanySalaries(
        selectedMonth,
        user?.partnership_id || 1,
      );
      console.log("Company salaries response:", response);
      if (response.success) {
        const salariesData = response.data || [];
        console.log("Company salaries data:", salariesData);

        // Filter only חברה employees and sort by name
        const companySalaries = salariesData
          .filter((s) => s.employee_type === "חברה")
          .sort((a, b) =>
            (a.employee_name || "").localeCompare(b.employee_name || "", "he"),
          );

        setSalaries(companySalaries);

        // Extract unique customers for filter dropdown (from company salaries only)
        const uniqueCustomers = [
          ...new Set(
            companySalaries.map((s) => s.customer_name).filter(Boolean),
          ),
        ].sort((a, b) => a.localeCompare(b, "he"));
        setCustomers(uniqueCustomers);

        // Extract unique sites for filter dropdown
        const uniqueSites = [
          ...new Set(companySalaries.map((s) => s.site_name).filter(Boolean)),
        ].sort((a, b) => a.localeCompare(b, "he"));
        setSites(uniqueSites);

        // Extract unique payment types for filter dropdown
        const uniquePaymentTypes = [
          ...new Set(
            companySalaries.map((s) => s.payment_type).filter(Boolean),
          ),
        ].sort((a, b) => a.localeCompare(b, "he"));
        setPaymentTypes(uniquePaymentTypes);
      }
    } catch (error) {
      console.error("Failed to load salaries:", error);
      setSalaries([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await apiService.getCompanySalarySummary(
        selectedMonth,
        user?.partnership_id || 1,
      );
      if (response.success) {
        setSummary(response);
      }
    } catch (error) {
      console.error("Failed to load summary:", error);
    }
  };

  const handleUpdateToPay = async (salary) => {
    const newValue = editingToPay[salary.id];

    if (!newValue || isNaN(newValue)) {
      alert("אנא הזן סכום תקין");
      return;
    }

    const confirmed = window.confirm(
      `האם אתה בטוח שברצונך לשנות את סה"כ לתשלום עבור ${
        salary.employee_name
      }?\n\nמ-₪${parseFloat(salary.to_pay).toFixed(2)} ל-₪${parseFloat(
        newValue,
      ).toFixed(2)}`,
    );

    if (!confirmed) {
      setEditingToPay((prev) => {
        const updated = { ...prev };
        delete updated[salary.id];
        return updated;
      });
      return;
    }

    try {
      const data = { to_pay: parseFloat(newValue) };
      const response = await apiService.updateCompanySalary(salary.id, data);

      if (response.success) {
        setSalaries((prevSalaries) =>
          prevSalaries.map((s) =>
            s.id === salary.id ? { ...s, to_pay: parseFloat(newValue) } : s,
          ),
        );
        setEditingToPay((prev) => {
          const updated = { ...prev };
          delete updated[salary.id];
          return updated;
        });
        await loadSummary();
        alert('✅ סה"כ לתשלום עודכן בהצלחה');
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to update to_pay:", error);
      alert('שגיאה בעדכון סה"כ לתשלום');
    }
  };

  const handleUpdateSalary = async (salaryId, field, value) => {
    try {
      const data = { [field]: value };
      const response = await apiService.updateCompanySalary(salaryId, data);

      if (response.success) {
        setSalaries((prevSalaries) =>
          prevSalaries.map((salary) =>
            salary.id === salaryId ? { ...salary, [field]: value } : salary,
          ),
        );

        if (field === "paid" || field === "extras") {
          await loadSummary();
        }
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to update salary:", error);
      alert("שגיאה בעדכון משכורת");
    }
  };

  const handleUpdatePaymentMethod = async (salary, newPaymentMethod) => {
    try {
      // Update the employee's payment_method in the database
      const response = await apiService.updateEmployee(salary.employee_id, {
        payment_method: newPaymentMethod,
      });

      if (response.success) {
        // Update local state to reflect the change
        setSalaries((prevSalaries) =>
          prevSalaries.map((s) =>
            s.id === salary.id ? { ...s, payment_type: newPaymentMethod } : s,
          ),
        );
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to update payment method:", error);
      alert("שגיאה בעדכון צורת תשלום");
    }
  };

  const handleUpdateReceipt = async (salary) => {
    const newValue = editingReceipt[salary.id];

    try {
      const data = { receipt_number: newValue || "" };
      const response = await apiService.updateCompanySalary(salary.id, data);

      if (response.success) {
        setSalaries((prevSalaries) =>
          prevSalaries.map((s) =>
            s.id === salary.id ? { ...s, receipt_number: newValue || "" } : s,
          ),
        );
        setEditingReceipt((prev) => {
          const updated = { ...prev };
          delete updated[salary.id];
          return updated;
        });
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to update receipt number:", error);
      alert("שגיאה בעדכון מספר אסמכתא");
    }
  };

  const startEdit = (salary) => {
    setEditingId(salary.id);
    setEditForm({
      regular_hours: salary.regular_hours || 0,
      weekend_hours: salary.weekend_hours || 0,
      hour_rate: salary.hour_rate || 0,
      weekend_hour_rate: salary.weekend_hour_rate || 0,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (salaryId) => {
    try {
      // Calculate new salary based on edited hours and rates
      const regularPay =
        parseFloat(editForm.regular_hours || 0) *
        parseFloat(editForm.hour_rate || 0);
      const weekendPay =
        parseFloat(editForm.weekend_hours || 0) *
        parseFloat(editForm.weekend_hour_rate || 0);
      const newSalary = regularPay + weekendPay;

      const updateData = {
        regular_hours: parseFloat(editForm.regular_hours),
        weekend_hours: parseFloat(editForm.weekend_hours),
        hour_rate: parseFloat(editForm.hour_rate),
        weekend_hour_rate: parseFloat(editForm.weekend_hour_rate),
        salary: newSalary,
        total_hours:
          parseFloat(editForm.regular_hours) +
          parseFloat(editForm.weekend_hours),
      };

      const response = await apiService.updateCompanySalary(
        salaryId,
        updateData,
      );

      if (response.success) {
        await loadSalaries();
        await loadSummary();
        setEditingId(null);
        setEditForm({});
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to save edit:", error);
      alert("שגיאה בשמירת עדכון");
    }
  };

  const handleAddSalary = async () => {
    if (!newSalaryForm.employee_id) {
      alert("נא לבחור עובד");
      return;
    }

    if (
      !newSalaryForm.salary_amount ||
      parseFloat(newSalaryForm.salary_amount) <= 0
    ) {
      alert("נא להזין סכום משכורת");
      return;
    }

    try {
      const employee = employees.find(
        (e) => e.employee_id === parseInt(newSalaryForm.employee_id),
      );
      if (!employee) {
        alert("עובד לא נמצא");
        return;
      }

      const regularHours = parseFloat(newSalaryForm.regular_hours || 0);
      const weekendHours = parseFloat(newSalaryForm.weekend_hours || 0);
      const totalHours = regularHours + weekendHours;
      const salaryAmount = parseFloat(newSalaryForm.salary_amount);

      const salaryData = {
        employee_id: employee.employee_id,
        month: selectedMonth,
        regular_hours: regularHours,
        weekend_hours: weekendHours,
        total_hours: totalHours,
        hour_rate: employee.hour_rate || 0,
        weekend_hour_rate: employee.weekend_hour_rate || 0,
        salary: salaryAmount,
        extras: 0,
        total_to_pay: salaryAmount,
        paid: false,
        employee_type: employee.type,
      };

      const response = await apiService.createSalary(salaryData);

      if (response.success) {
        await loadSalaries();
        await loadSummary();
        setShowAddForm(false);
        setNewSalaryForm({
          employee_id: "",
          regular_hours: "",
          weekend_hours: "",
          salary_amount: "",
        });
      } else {
        alert(`שגיאה: ${response.error || "יצירה נכשלה"}`);
      }
    } catch (error) {
      console.error("Failed to add salary:", error);
      alert("שגיאה בהוספת משכורת");
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const openEmployeeModal = async (salary) => {
    try {
      const response = await apiService.getEmployee(salary.employee_id);
      const employee = response.employee || response.data || response;
      setSelectedEmployee(employee);
    } catch (error) {
      console.error("Failed to load employee:", error);
      setSelectedEmployee({
        employee_id: salary.employee_id,
        employee_name: salary.employee_name,
      });
    }
    setEmployeeModalOpen(true);
  };

  const getFilteredSalaries = () => {
    return salaries.filter((salary) => {
      const matchesName =
        searchName === "" ||
        (salary.employee_name || "")
          .toLowerCase()
          .includes(searchName.toLowerCase());

      const matchesCustomer =
        selectedCustomer === "" || salary.customer_name === selectedCustomer;

      const matchesSite =
        selectedSite === "" || salary.site_name === selectedSite;

      const matchesPaymentType =
        selectedPaymentType === "" ||
        salary.payment_type === selectedPaymentType;

      return (
        matchesName && matchesCustomer && matchesSite && matchesPaymentType
      );
    });
  };

  const openExtrasModal = (salary) => {
    setSelectedSalary(salary);
    setExtrasModalOpen(true);
  };

  const closeExtrasModal = () => {
    setExtrasModalOpen(false);
    setSelectedSalary(null);
    loadSalaries();
    loadSummary();
  };

  const filteredSalaries = getFilteredSalaries();
  const companySummary = summary
    ? {
        total: filteredSalaries.length,
        totalToPay: filteredSalaries.reduce(
          (sum, s) => sum + (parseFloat(s.to_pay) || 0),
          0,
        ),
        paid: filteredSalaries.filter((s) => s.paid).length,
        paidAmount: filteredSalaries
          .filter((s) => s.paid)
          .reduce((sum, s) => sum + (parseFloat(s.to_pay) || 0), 0),
      }
    : null;

  return (
    <div className='salaries-container company-salaries'>
      <div className='salaries-header'>
        <div>
          <h1>ניהול משכורות חברה</h1>
          <p>ניהול ידני של משכורות עובדי חברה</p>
        </div>
        <div className='header-actions'>
          <div className='month-selector'>
            <Calendar size={18} />
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value=''>בחר חודש</option>
              {availableMonths.map((month) => (
                <option key={month} value={month}>
                  {formatMonth(month)}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className='add-salary-btn'
            title='הוסף משכורת'
          >
            <Plus size={18} />
            הוסף משכורת
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className='add-salary-form'>
          <div className='form-header'>
            <h3>הוסף משכורת חדשה</h3>
            <button onClick={() => setShowAddForm(false)} className='close-btn'>
              <X size={18} />
            </button>
          </div>
          <div className='form-content'>
            <div className='form-group'>
              <label>
                עובד חברה <span className='required'>*</span>
              </label>
              <select
                value={newSalaryForm.employee_id}
                onChange={(e) =>
                  setNewSalaryForm({
                    ...newSalaryForm,
                    employee_id: e.target.value,
                  })
                }
                required
              >
                <option value=''>בחר עובד</option>
                {employees
                  .filter((emp) => {
                    const type = emp.type || emp.employee_type || "";
                    return type.includes("חברה");
                  })
                  .map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {emp.employee_name} - {emp.customer_name} (
                      {emp.type || emp.employee_type})
                    </option>
                  ))}
              </select>
              <small
                style={{
                  color: "#64748b",
                  fontSize: "0.8rem",
                  marginTop: "0.25rem",
                }}
              >
                חודש:{" "}
                {selectedMonth
                  ? new Date(selectedMonth + "-01").toLocaleDateString(
                      "he-IL",
                      { year: "numeric", month: "long" },
                    )
                  : ""}
              </small>
            </div>
            <div className='form-row'>
              <div className='form-group'>
                <label>שעות רגילות</label>
                <input
                  type='number'
                  step='0.01'
                  min='0'
                  placeholder='0.00'
                  value={newSalaryForm.regular_hours}
                  onChange={(e) =>
                    setNewSalaryForm({
                      ...newSalaryForm,
                      regular_hours: e.target.value,
                    })
                  }
                />
              </div>
              <div className='form-group'>
                <label>שעות סופ״ש</label>
                <input
                  type='number'
                  step='0.01'
                  min='0'
                  placeholder='0.00'
                  value={newSalaryForm.weekend_hours}
                  onChange={(e) =>
                    setNewSalaryForm({
                      ...newSalaryForm,
                      weekend_hours: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className='form-group'>
              <label>
                סכום משכורת <span className='required'>*</span>
              </label>
              <input
                type='number'
                step='0.01'
                min='0'
                placeholder='0.00'
                value={newSalaryForm.salary_amount}
                onChange={(e) =>
                  setNewSalaryForm({
                    ...newSalaryForm,
                    salary_amount: e.target.value,
                  })
                }
                required
              />
            </div>
            <div className='form-actions'>
              <button onClick={handleAddSalary} className='save-btn'>
                <Save size={18} />
                שמור משכורת
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className='cancel-btn'
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {companySummary && (
        <div className='summary-cards'>
          <div className='summary-card'>
            <div
              className='summary-icon'
              style={{ background: "#dbeafe", color: "#3b82f6" }}
            >
              <Users size={24} />
            </div>
            <div className='summary-content'>
              <p className='summary-label'>סה״כ עובדים</p>
              <h3 className='summary-value'>{companySummary.total}</h3>
            </div>
          </div>

          {(user?.permission === "admin" || user?.permission === "owner") && (
            <>
              <div className='summary-card'>
                <div
                  className='summary-icon'
                  style={{ background: "#dcfce7", color: "#10b981" }}
                >
                  <DollarSign size={24} />
                </div>
                <div className='summary-content'>
                  <p className='summary-label'>סה״כ לתשלום</p>
                  <h3 className='summary-value'>
                    ₪
                    {companySummary.totalToPay.toLocaleString("he-IL", {
                      maximumFractionDigits: 0,
                    })}
                  </h3>
                </div>
              </div>
              <div className='summary-card paid'>
                <div
                  className='summary-icon'
                  style={{ background: "#fce7f3", color: "#ec4899" }}
                >
                  <DollarSign size={24} />
                </div>
                <div className='summary-content'>
                  <p className='summary-label'>שולם</p>
                  <h3 className='summary-value'>
                    ₪
                    {companySummary.paidAmount.toLocaleString("he-IL", {
                      maximumFractionDigits: 0,
                    })}
                  </h3>
                  <p className='summary-sublabel'>
                    {companySummary.paid} עובדים
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {!loading && salaries.length > 0 && (
        <div className='filters-section'>
          <div className='search-box'>
            <Search size={18} />
            <input
              type='text'
              placeholder='חיפוש לפי שם עובד...'
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>
          <div className='customer-filter'>
            <Filter size={18} />
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
            >
              <option value=''>כל הלקוחות</option>
              {customers.map((customer) => (
                <option key={customer} value={customer}>
                  {customer}
                </option>
              ))}
            </select>
          </div>
          <div className='customer-filter'>
            <Filter size={18} />
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
            >
              <option value=''>כל האתרים</option>
              {sites.map((site) => (
                <option key={site} value={site}>
                  {site}
                </option>
              ))}
            </select>
          </div>
          <div className='customer-filter'>
            <CreditCard size={18} />
            <select
              value={selectedPaymentType}
              onChange={(e) => setSelectedPaymentType(e.target.value)}
            >
              <option value=''>כל צורות התשלום</option>
              {paymentTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {loading ? (
        <div className='loading-state'>
          <div className='spinner'></div>
          <p>טוען נתונים...</p>
        </div>
      ) : filteredSalaries.length === 0 ? (
        <div className='empty-state'>
          <UserPlus size={48} />
          <h3>אין משכורות להצגה</h3>
          <p>לא נמצאו משכורות עבור החודש שנבחר</p>
          <button
            onClick={() => setShowAddForm(true)}
            className='add-salary-btn'
          >
            <Plus size={18} />
            הוסף משכורת ראשונה
          </button>
        </div>
      ) : (
        <div className='salaries-table-container'>
          <table className='salaries-table'>
            <thead>
              <tr>
                <th>שם עובד</th>
                <th>לקוח / אתר</th>
                <th>שעות רגילות</th>
                <th>שעות סופ״ש</th>
                <th>סה״כ שעות</th>
                <th>תעריף רגיל</th>
                <th>תעריף סופ״ש</th>
                <th>תוספות/ניכויים</th>
                <th>סה״כ לתשלום</th>
                <th>צורת תשלום</th>
                <th>מספר אסמכתא</th>
                <th>שולם</th>
                <th>הערות</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {filteredSalaries.map((salary) => (
                <tr key={salary.id}>
                  {editingId === salary.id ? (
                    <>
                      <td
                        className='clickable-cell'
                        onClick={() => openEmployeeModal(salary)}
                        title='לחץ לעריכת פרטי עובד'
                      >
                        {salary.employee_name}
                      </td>
                      <td className='customer-site-cell'>
                        <div className='customer-name'>
                          {salary.customer_name || "-"}
                        </div>
                        <div className='site-name'>
                          {salary.site_name || "-"}
                        </div>
                      </td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          value={editForm.regular_hours}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              regular_hours: e.target.value,
                            })
                          }
                          className='edit-input'
                        />
                      </td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          value={editForm.weekend_hours}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              weekend_hours: e.target.value,
                            })
                          }
                          className='edit-input'
                        />
                      </td>
                      <td>
                        {(
                          parseFloat(editForm.regular_hours || 0) +
                          parseFloat(editForm.weekend_hours || 0)
                        ).toFixed(2)}
                      </td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          value={editForm.hour_rate}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              hour_rate: e.target.value,
                            })
                          }
                          className='edit-input'
                        />
                      </td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          value={editForm.weekend_hour_rate}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              weekend_hour_rate: e.target.value,
                            })
                          }
                          className='edit-input'
                        />
                      </td>
                      <td>₪{(salary.extras || 0).toFixed(2)}</td>
                      <td>
                        ₪
                        {(
                          parseFloat(editForm.regular_hours || 0) *
                            parseFloat(editForm.hour_rate || 0) +
                          parseFloat(editForm.weekend_hours || 0) *
                            parseFloat(editForm.weekend_hour_rate || 0)
                        ).toFixed(2)}
                      </td>
                      <td className='payment-type-cell'>
                        <select
                          value={salary.payment_type || ""}
                          onChange={(e) =>
                            handleUpdatePaymentMethod(salary, e.target.value)
                          }
                          className='payment-type-select'
                        >
                          <option value=''>בחר צורת תשלום</option>
                          <option value='העברה בנקאית'>העברה בנקאית</option>
                          <option value="צ'ק">צ'ק</option>
                          <option value='קבלן'>קבלן</option>
                        </select>
                      </td>
                      <td>{salary.receipt_number || "-"}</td>
                      <td>
                        <input type='checkbox' checked={salary.paid} disabled />
                      </td>
                      <td>
                        {salary.employee_notes ? (
                          <MessageSquare size={14} />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className='actions-cell'>
                        <button
                          onClick={() => saveEdit(salary.id)}
                          className='save-btn-small'
                          title='שמור'
                        >
                          <Save size={16} />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className='cancel-btn-small'
                          title='בטל'
                        >
                          <X size={16} />
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td
                        className='clickable-cell'
                        onClick={() => openEmployeeModal(salary)}
                        title='לחץ לעריכת פרטי עובד'
                      >
                        {salary.employee_name}
                      </td>
                      <td className='customer-site-cell'>
                        <div className='customer-name'>
                          {salary.customer_name || "-"}
                        </div>
                        <div className='site-name'>
                          {salary.site_name || "-"}
                        </div>
                      </td>
                      <td>{(salary.total_regular_hours || 0).toFixed(2)}</td>
                      <td>{(salary.total_weekend_hours || 0).toFixed(2)}</td>
                      <td>
                        {(
                          (salary.total_regular_hours || 0) +
                          (salary.total_weekend_hours || 0)
                        ).toFixed(2)}
                      </td>
                      <td>₪{(salary.rate || 0).toFixed(2)}</td>
                      <td>₪{(salary.weekend_rate || 0).toFixed(2)}</td>
                      <td>
                        <button
                          onClick={() => openExtrasModal(salary)}
                          className='extras-btn'
                        >
                          ₪{(salary.extras || 0).toFixed(2)}
                        </button>
                      </td>
                      <td className='total-cell'>
                        {user?.permission === "admin" ||
                        user?.permission === "owner" ? (
                          <div className='editable-cell'>
                            <span>₪</span>
                            <input
                              type='number'
                              step='0.01'
                              value={
                                editingToPay[salary.id] !== undefined
                                  ? editingToPay[salary.id]
                                  : (salary.to_pay || 0).toFixed(2)
                              }
                              onChange={(e) =>
                                setEditingToPay((prev) => ({
                                  ...prev,
                                  [salary.id]: e.target.value,
                                }))
                              }
                              onBlur={() => {
                                if (
                                  editingToPay[salary.id] !== undefined &&
                                  editingToPay[salary.id] !==
                                    (salary.to_pay || 0).toFixed(2)
                                ) {
                                  handleUpdateToPay(salary);
                                } else {
                                  setEditingToPay((prev) => {
                                    const updated = { ...prev };
                                    delete updated[salary.id];
                                    return updated;
                                  });
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  e.target.blur();
                                }
                              }}
                              className='to-pay-input'
                              title='לחץ להזנת סכום חדש'
                            />
                          </div>
                        ) : (
                          <span>₪{(salary.to_pay || 0).toFixed(2)}</span>
                        )}
                      </td>
                      <td className='payment-type-cell'>
                        <select
                          value={salary.payment_type || ""}
                          onChange={(e) =>
                            handleUpdatePaymentMethod(salary, e.target.value)
                          }
                          className='payment-type-select'
                        >
                          <option value=''>בחר צורת תשלום</option>
                          <option value='העברה בנקאית'>העברה בנקאית</option>
                          <option value="צ'ק">צ'ק</option>
                          <option value='קבלן'>קבלן</option>
                        </select>
                      </td>
                      <td className='receipt-cell'>
                        <input
                          type='text'
                          value={
                            editingReceipt[salary.id] !== undefined
                              ? editingReceipt[salary.id]
                              : salary.receipt_number || ""
                          }
                          onChange={(e) =>
                            setEditingReceipt((prev) => ({
                              ...prev,
                              [salary.id]: e.target.value,
                            }))
                          }
                          onBlur={() => {
                            if (
                              editingReceipt[salary.id] !== undefined &&
                              editingReceipt[salary.id] !==
                                (salary.receipt_number || "")
                            ) {
                              handleUpdateReceipt(salary);
                            } else {
                              setEditingReceipt((prev) => {
                                const updated = { ...prev };
                                delete updated[salary.id];
                                return updated;
                              });
                            }
                          }}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              e.target.blur();
                            }
                          }}
                          className='receipt-input'
                          placeholder='מספר אסמכתא'
                        />
                      </td>
                      <td>
                        <input
                          type='checkbox'
                          checked={salary.paid}
                          onChange={(e) =>
                            handleUpdateSalary(
                              salary.id,
                              "paid",
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                      <td
                        className={`notes-cell ${salary.employee_notes ? "has-notes clickable-cell" : ""}`}
                        onClick={() => {
                          if (salary.employee_notes) {
                            const notesList = salary.employee_notes
                              .split("|")
                              .map((n) => n.trim());
                            setNotesPopupData({
                              employeeName: salary.employee_name,
                              notes: notesList,
                            });
                            setNotesPopupOpen(true);
                          }
                        }}
                        title={salary.employee_notes ? "לחץ לצפייה בהערות" : ""}
                      >
                        {salary.employee_notes ? (
                          <div className='notes-indicator'>
                            <MessageSquare size={16} />
                            <span className='notes-count'>
                              {salary.employee_notes.split("|").length}
                            </span>
                          </div>
                        ) : (
                          <span className='no-notes'>-</span>
                        )}
                      </td>
                      <td className='actions-cell'>
                        <button
                          onClick={() => startEdit(salary)}
                          className='edit-btn-small'
                          title='ערוך שעות'
                        >
                          <Edit2 size={16} />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {extrasModalOpen && selectedSalary && (
        <ExtrasModal
          isOpen={extrasModalOpen}
          onClose={closeExtrasModal}
          salary={selectedSalary}
          onUpdate={closeExtrasModal}
        />
      )}

      {/* Payment Details Popup */}
      {paymentPopup.visible && paymentPopup.salary && (
        <div
          className='payment-popup'
          style={{
            position: "fixed",
            left: paymentPopup.position.x,
            top: paymentPopup.position.y,
            zIndex: 1000,
            background: "white",
            border: "1px solid #ddd",
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            minWidth: "200px",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              marginBottom: "8px",
              borderBottom: "1px solid #eee",
              paddingBottom: "4px",
            }}
          >
            פרטי תשלום
          </div>
          <div style={{ fontSize: "14px" }}>
            <div>
              <strong>צורת תשלום:</strong>{" "}
              {paymentPopup.salary.payment_type || "-"}
            </div>
            {paymentPopup.salary.bank && (
              <div>
                <strong>בנק:</strong> {paymentPopup.salary.bank}
              </div>
            )}
            {paymentPopup.salary.branch && (
              <div>
                <strong>סניף:</strong> {paymentPopup.salary.branch}
              </div>
            )}
            {paymentPopup.salary.account && (
              <div>
                <strong>חשבון:</strong> {paymentPopup.salary.account}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes Popup Modal */}
      {notesPopupOpen && notesPopupData && (
        <div
          className='notes-popup-overlay'
          onClick={() => setNotesPopupOpen(false)}
        >
          <div
            className='notes-popup-content'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='notes-popup-header'>
              <h3>הערות - {notesPopupData.employeeName}</h3>
              <button
                className='notes-popup-close'
                onClick={() => setNotesPopupOpen(false)}
              >
                ×
              </button>
            </div>
            <div className='notes-popup-list'>
              {notesPopupData.notes.map((note, index) => {
                const isMonthly = note.includes("[חודשי]");
                const noteText = note
                  .replace("[חודשי]", "")
                  .replace("[קבוע]", "")
                  .trim();
                const noteType = isMonthly ? "חודשי" : "קבוע";
                return (
                  <div key={index} className='notes-popup-item'>
                    <div className='notes-popup-item-type'>
                      <span
                        className={`note-type-badge ${isMonthly ? "monthly" : "permanent"}`}
                      >
                        {noteType}
                      </span>
                    </div>
                    <div className='notes-popup-item-text'>{noteText}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Employee Edit Modal */}
      {employeeModalOpen && selectedEmployee && (
        <EmployeeModal
          employee={selectedEmployee}
          onClose={(reload) => {
            setEmployeeModalOpen(false);
            setSelectedEmployee(null);
            if (reload) {
              loadSalaries();
              loadSummary();
              loadCompanyEmployees();
            }
          }}
        />
      )}
    </div>
  );
};

export default CompanySalaries;
